<template>
  <div class="search-filter-container">
    <div class="primary-controls">
      <!-- Search Field -->
      <v-text-field
        :value="searchQuery"
        @input="$emit('update:search-query', $event)"
        prepend-inner-icon="mdi-magnify"
        label="Search provinces..."
        variant="outlined"
        class="search-field"
        clearable
        color="#fddf00"
        density="comfortable"
      ></v-text-field>

      <!-- Filter Controls -->
      <div class="filter-controls">
        <v-btn
          @click="$emit('toggle-orders-filter')"
          :color="showOnlyWithOrders ? '#fddf00' : 'grey-lighten-1'"
          :variant="showOnlyWithOrders ? 'flat' : 'outlined'"
          class="filter-orders-btn"
          density="comfortable"
        >
          <v-icon start class="mr-1">{{
            showOnlyWithOrders ? "mdi-filter-check" : "mdi-filter"
          }}</v-icon>
          {{ showOnlyWithOrders ? "Show All" : "Orders Only" }}
        </v-btn>

        <v-btn
          @click="$emit('toggle-pinned-filter')"
          :color="showOnlyPinned ? '#fddf00' : 'grey-lighten-1'"
          :variant="showOnlyPinned ? 'flat' : 'outlined'"
          class="filter-pinned-btn"
          density="comfortable"
        >
          <v-icon start class="mr-1">{{
            showOnlyPinned ? "mdi-pin" : "mdi-pin-outline"
          }}</v-icon>
          {{ showOnlyPinned ? "Show All" : "Pinned Only" }}
        </v-btn>

        <v-btn
          @click="$emit('refresh-data')"
          color="#fddf00"
          variant="flat"
          class="refresh-btn"
          density="comfortable"
          :loading="isRefreshing"
        >
          <v-icon start class="mr-1">mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </div>

      <!-- Grid Display Options -->
      <div class="grid-display-options">
        <v-btn-toggle
          :model-value="gridDisplay"
          @update:model-value="$emit('update:grid-display', $event)"
          color="#fddf00"
          variant="outlined"
          mandatory
          class="grid-toggle"
          density="compact"
        >
          <v-btn value="5" size="small" class="grid-btn">
            <v-icon size="small">mdi-view-grid</v-icon>
            <span class="btn-label">5</span>
          </v-btn>
          <v-btn value="3" size="small" class="grid-btn">
            <v-icon size="small">mdi-view-module</v-icon>
            <span class="btn-label">3</span>
          </v-btn>
          <v-btn value="list" size="small" class="grid-btn">
            <v-icon size="small">mdi-view-list</v-icon>
            <span class="btn-label">List</span>
          </v-btn>
        </v-btn-toggle>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SearchFilterBar",
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
    showOnlyWithOrders: {
      type: Boolean,
      default: false,
    },
    showOnlyPinned: {
      type: Boolean,
      default: false,
    },
    gridDisplay: {
      type: String,
      default: "5",
    },
    isRefreshing: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "update:search-query",
    "update:grid-display",
    "toggle-orders-filter",
    "toggle-pinned-filter",
    "refresh-data",
  ],
};
</script>

<style scoped>
.search-filter-container {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1.5rem;
  margin: 0 auto;
  border: 1px solid rgba(253, 223, 0, 0.2);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.primary-controls {
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
}

.search-field {
  flex: 1;
  max-width: none;
}

.filter-controls {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-shrink: 0;
}

.filter-orders-btn,
.filter-pinned-btn,
.refresh-btn {
  height: 48px;
  font-weight: 600;
  border-radius: 8px;
  text-transform: none;
  letter-spacing: 0;
  white-space: nowrap;
}

.filter-orders-btn .v-icon,
.filter-pinned-btn .v-icon,
.refresh-btn .v-icon {
  opacity: 1 !important;
  font-size: 1.2rem !important;
}

.filter-orders-btn,
.filter-pinned-btn {
  color: #2c3e50 !important;
  min-width: 120px;
}

.filter-orders-btn .v-icon,
.filter-pinned-btn .v-icon {
  color: inherit !important;
}

.refresh-btn {
  color: black !important;
  min-width: 100px;
}

.refresh-btn .v-icon {
  color: black !important;
}

.grid-display-options {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.grid-toggle {
  border: 2px solid rgba(253, 223, 0, 0.3);
  border-radius: 10px;
  overflow: hidden;
  height: 48px;
}

.grid-btn {
  min-width: 60px !important;
  border-radius: 0 !important;
  color: #6b7280 !important;
  font-weight: 500;
  height: 44px;
}

.grid-btn.v-btn--active {
  background-color: #fddf00 !important;
  color: black !important;
}

.btn-label {
  margin-left: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
}

@media (max-width: 768px) {
  .search-filter-container {
    padding: 1rem;
    border-radius: 12px;
  }

  .primary-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    justify-content: stretch;
  }

  .search-field {
    max-width: 100%;
  }

  .filter-controls {
    justify-content: center;
    flex-wrap: wrap;
    order: 1;
  }

  .grid-display-options {
    justify-content: center;
    order: 2;
  }

  .grid-toggle {
    width: 100%;
    max-width: 280px;
  }

  .grid-btn {
    flex: 1;
    min-width: auto !important;
  }
}

@media (max-width: 480px) {
  .search-filter-container {
    margin: 0 0.5rem;
    padding: 0.75rem;
  }

  .filter-controls {
    flex-direction: column;
    gap: 0.5rem;
  }

  .filter-orders-btn,
  .filter-pinned-btn,
  .refresh-btn {
    min-width: auto;
    width: calc(50% - 0.375rem);
  }

  .refresh-btn {
    width: 100%;
    margin-top: 0.5rem;
  }
}
</style>
